/**
 * A header's words (SA-1124, shared since SA-1185): a sort button on a
 * sortable column, else the text; the column's `title` rides the one tooltip
 * helper, never a native `title` (in the register the resize handle's tip
 * would show beside it, SA-1086 item 4). The glyph shows the sorted
 * direction, and on any other sortable column appears only on hover or focus.
 * Tailwind's reset takes text-transform off a button, so the register passes
 * `uppercase` as `className`; Rotations and Contracts pass nothing.
 * SA-1193: an unsorted titled header's text is a focusable span, so a keyboard
 * reaches its tip as it reaches a sort button's. `wrap` is for a table with
 * automatic layout (Rotations, Contracts): the label may break across lines,
 * aligned as its header cell is (a button centers its text). The register's
 * fixed-width shell passes no `wrap` and keeps its ellipsis.
 */
import { ArrowDown, ArrowUp, ArrowUpDown } from 'lucide-react'
import { Tip } from '@/components/ui'
import { cn } from '@/utils'
import type { SortDir } from './registerSort'

export function SortHeaderLabel({ header, title, sorted, onSort, className, wrap = false }: {
  header: string
  title?: string
  sorted: SortDir | null
  onSort?: () => void
  className?: string
  wrap?: boolean
}) {
  if (!onSort) {
    if (!title) return <>{header}</>
    return <Tip text={title}><span tabIndex={0}>{header}</span></Tip>
  }
  const Glyph = sorted === 'asc' ? ArrowUp : sorted === 'desc' ? ArrowDown : ArrowUpDown
  const button = (
    <button type="button" onClick={onSort}
      className={cn(
        'group/sort inline-flex max-w-full items-center gap-1 rounded-sm',
        wrap && '[text-align:inherit]',
        className,
        'hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        sorted && 'text-foreground',
      )}>
      <span className={wrap ? undefined : 'truncate'}>{header}</span>
      <Glyph aria-hidden="true" className={cn('h-3 w-3 shrink-0',
        !sorted && 'opacity-0 group-hover/sort:opacity-100 group-focus-visible/sort:opacity-100')} />
    </button>
  )
  return title ? <Tip text={title}>{button}</Tip> : button
}
